import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import ClienteQuickCreateModal from '../ClienteQuickCreateModal';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { getMomentLocale } from '../../translate/locale';

const validationSchema = Yup.object().shape({
  dataInicio: Yup.date().required('Data de início é obrigatória'),
  dataFim: Yup.date().when('tipo', (tipo, schema) => {
    const currentTipo = Array.isArray(tipo) ? tipo[0] : tipo;

    if (currentTipo === 'followup') {
      return schema.nullable();
    }

    return schema
      .required('Data de término é obrigatória')
      .min(Yup.ref('dataInicio'), 'Data de término deve ser posterior à data de início');
  }),
});

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: theme.spacing(1.5),
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.5),
  },
  reminderRow: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  actions: {
    padding: theme.spacing(1.5, 3, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  saveButton: {
    minWidth: 120,
  },
}));

const AgendamentoModal = ({ open, onClose, agendamento, onSave, forceFollowupType = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    dataInicio: moment().toDate(),
    dataFim: moment().add(1, 'hour').toDate(),
    tipo: 'followup',
    status: 'pendente',
    prioridade: 'media',
    local: '',
    observacoes: '',
    resultado: '',
    clienteId: null,
    contatoId: null,
    historicoId: null,
    lembrete: true,
    tempoLembrete: 15,
  });

  const [clientes, setClientes] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingMoreClientes, setLoadingMoreClientes] = useState(false);
  const [clienteSearchTerm, setClienteSearchTerm] = useState('');
  const [clientesPage, setClientesPage] = useState(1);
  const [clientesHasMore, setClientesHasMore] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const clienteSearchDebounceRef = useRef(null);
  const clienteSearchRequestRef = useRef(0);
  const contatosRequestRef = useRef(0);
  const selectedClienteRef = useRef(null);
  const isFollowup = forceFollowupType || formData.tipo === 'followup';

  const CLIENTE_SEARCH_DEBOUNCE_MS = 350;

  const mergeClientesWithSelection = (fetchedClientes, currentSelection) => {
    const normalizedFetched = Array.isArray(fetchedClientes) ? fetchedClientes : [];

    if (!currentSelection?.CODCLI) {
      return normalizedFetched;
    }

    const hasSelectionInList = normalizedFetched.some(
      (cliente) => String(cliente?.CODCLI) === String(currentSelection.CODCLI)
    );

    if (hasSelectionInList) {
      return normalizedFetched;
    }

    return [currentSelection, ...normalizedFetched];
  };

  const normalizeContatosResponse = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.contatos)) {
      return payload.contatos;
    }

    return [];
  };

  const getContatoPhone = (ddd, numero) => {
    const normalizedDdd = String(ddd || '').trim();
    const normalizedNumero = String(numero || '').trim();

    if (!normalizedNumero) {
      return '';
    }

    return normalizedDdd ? `(${normalizedDdd}) ${normalizedNumero}` : normalizedNumero;
  };

  const getContatoDisplayInfo = (contato) => {
    const celular = getContatoPhone(contato?.ddd_celular, contato?.celular);
    if (celular) {
      return celular;
    }

    const telefone = getContatoPhone(contato?.ddd_telefone, contato?.telefone);
    if (telefone) {
      return telefone;
    }

    return String(contato?.email || '').trim();
  };

  useEffect(() => {
    selectedClienteRef.current = selectedCliente;
  }, [selectedCliente]);

  useEffect(() => {
    if (open) {
      setClienteSearchTerm('');
      setClientesPage(1);
      setClientesHasMore(false);
      queueLoadClientes('', true);
      
      if (agendamento) {
        setFormData({
          titulo: agendamento.titulo || '',
          descricao: agendamento.descricao || '',
          dataInicio: agendamento.dataInicio ? moment(agendamento.dataInicio).toDate() : moment().toDate(),
          dataFim: agendamento.dataFim ? moment(agendamento.dataFim).toDate() : moment().add(1, 'hour').toDate(),
          tipo: forceFollowupType ? 'followup' : (agendamento.tipo || 'followup'),
          status: agendamento.status || 'pendente',
          prioridade: agendamento.prioridade || 'media',
          local: agendamento.local || '',
          observacoes: agendamento.observacoes || '',
          resultado: agendamento.resultado || '',
          clienteId: agendamento.clienteId || null,
          contatoId: agendamento.contatoId || null,
          historicoId: agendamento.historicoId || null,
          lembrete: agendamento.lembrete !== undefined ? agendamento.lembrete : true,
          tempoLembrete: agendamento.tempoLembrete || 15,
        });

        // Quem abre o modal já sabendo o contato manda o objeto junto; a lista
        // real do cliente substitui esse item assim que a requisição volta
        if (agendamento.contato) {
          setContatos([agendamento.contato]);
        }

        if (agendamento.cliente) {
          setSelectedCliente(agendamento.cliente);
        } else if (!agendamento.clienteId) {
          // O modal fica montado entre aberturas: sem cliente no seed é preciso
          // limpar, senão a seleção da vez anterior reaparece
          setSelectedCliente(null);
          setContatos([]);
        }
      } else {
        // Reset para valores iniciais
        setFormData({
          titulo: '',
          descricao: '',
          dataInicio: moment().toDate(),
          dataFim: moment().add(1, 'hour').toDate(),
          tipo: 'followup',
          status: 'pendente',
          prioridade: 'media',
          local: '',
          observacoes: '',
          resultado: '',
          clienteId: null,
          contatoId: null,
          historicoId: null,
          lembrete: true,
          tempoLembrete: 15,
        });
        setSelectedCliente(null);
        setContatos([]);
      }
    } else {
      if (clienteSearchDebounceRef.current) {
        clearTimeout(clienteSearchDebounceRef.current);
        clienteSearchDebounceRef.current = null;
      }

      clienteSearchRequestRef.current += 1;
      contatosRequestRef.current += 1;
      setLoadingClientes(false);
      setLoadingMoreClientes(false);
    }
  }, [open, agendamento]);

  useEffect(() => {
    if (selectedCliente) {
      loadContatos(selectedCliente.CODCLI);
    }
  }, [selectedCliente]);

  useEffect(() => () => {
    if (clienteSearchDebounceRef.current) {
      clearTimeout(clienteSearchDebounceRef.current);
      clienteSearchDebounceRef.current = null;
    }
  }, []);

  const loadClientes = async ({ searchParam = '', page = 1, append = false } = {}) => {
    const requestId = ++clienteSearchRequestRef.current;
    const normalizedSearchParam = String(searchParam || '').trim();

    if (append) {
      setLoadingMoreClientes(true);
    } else {
      setLoadingClientes(true);
    }

    try {
      const { data } = await api.get('/clientes', {
        params: {
          searchParam: normalizedSearchParam || undefined,
          pageNumber: page,
        },
      });

      if (requestId !== clienteSearchRequestRef.current) {
        return;
      }

      const fetchedClientes = Array.isArray(data?.clientes) ? data.clientes : [];

      setClientes((prev) => {
        const baseClientes = append ? [...prev, ...fetchedClientes] : fetchedClientes;
        const dedupedClientes = baseClientes.filter((cliente, index, array) =>
          array.findIndex((item) => String(item?.CODCLI) === String(cliente?.CODCLI)) === index
        );

        return mergeClientesWithSelection(dedupedClientes, selectedClienteRef.current);
      });
      setClientesPage(page);
      setClientesHasMore(Boolean(data?.hasMore));
    } catch (err) {
      if (requestId !== clienteSearchRequestRef.current) {
        return;
      }

      toastError(err);
    } finally {
      if (requestId !== clienteSearchRequestRef.current) {
        return;
      }

      setLoadingClientes(false);
      setLoadingMoreClientes(false);
    }
  };

  const queueLoadClientes = (searchParam = '', immediate = false) => {
    const normalizedSearchParam = String(searchParam || '');
    setClienteSearchTerm(normalizedSearchParam);

    if (clienteSearchDebounceRef.current) {
      clearTimeout(clienteSearchDebounceRef.current);
      clienteSearchDebounceRef.current = null;
    }

    if (immediate) {
      loadClientes({ searchParam: normalizedSearchParam, page: 1, append: false });
      return;
    }

    clienteSearchDebounceRef.current = setTimeout(() => {
      loadClientes({ searchParam: normalizedSearchParam, page: 1, append: false });
    }, CLIENTE_SEARCH_DEBOUNCE_MS);
  };

  const loadMoreClientes = () => {
    if (loadingClientes || loadingMoreClientes || !clientesHasMore) {
      return;
    }

    loadClientes({
      searchParam: clienteSearchTerm,
      page: clientesPage + 1,
      append: true,
    });
  };

  const loadContatos = async (clienteId) => {
    const requestId = ++contatosRequestRef.current;

    try {
      const { data } = await api.get(`/clientes/${clienteId}/contatos`);
      const contatosData = normalizeContatosResponse(data);

      if (requestId !== contatosRequestRef.current) {
        return;
      }

      setContatos(contatosData);
      setFormData((prev) => {
        if (!prev.contatoId) {
          return prev;
        }

        const hasSelectedContato = contatosData.some(
          (contato) => String(contato.id) === String(prev.contatoId)
        );

        if (hasSelectedContato) {
          return prev;
        }

        return { ...prev, contatoId: null };
      });
    } catch (err) {
      if (requestId !== contatosRequestRef.current) {
        return;
      }

      console.error('Erro ao carregar contatos:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const nextFormData = { ...prev, [field]: value };

      // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: no follow-up usamos somente "Data", então sincroniza dataFim automaticamente.
      if (field === 'dataInicio' && (prev.tipo === 'followup' || value === null)) {
        nextFormData.dataFim = value;
      }

      if (field === 'tipo' && value === 'followup') {
        nextFormData.dataFim = prev.dataInicio;
      }

      return nextFormData;
    });

    // Limpar erro do campo ao alterar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    if (field === 'tipo' && value === 'followup' && errors.dataFim) {
      setErrors(prev => ({ ...prev, dataFim: null }));
    }
  };

  const validate = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach(error => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validate();
    if (!isValid) return;

    setSubmitting(true);
    try {
      // 20/04/2026 | Motivo: no fluxo de /followup, persistir sempre com tipo followup.
      const payload = {
        ...formData,
        tipo: isFollowup ? 'followup' : formData.tipo,
        dataFim: isFollowup ? formData.dataInicio : formData.dataFim,
        titulo: formData.titulo?.trim() || `Follow-up - ${moment(formData.dataInicio).format('DD/MM/YYYY HH:mm')}`,
      };

      if (agendamento?.id) {
        await api.put(`/agendamentos/${agendamento.id}`, payload);
        toast.success('Agendamento atualizado com sucesso!');
      } else {
        await api.post('/agendamentos', payload);
        toast.success('Agendamento criado com sucesso!');
      }
      onSave();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClienteCreated = (novoCliente) => {
    setQuickCreateOpen(false);
    setClientes((prev) => {
      const already = prev.some((c) => String(c.CODCLI) === String(novoCliente.CODCLI));
      return already ? prev : [novoCliente, ...prev];
    });
    setSelectedCliente(novoCliente);
    handleChange('clienteId', novoCliente.CODCLI);
    contatosRequestRef.current += 1;
    handleChange('contatoId', null);
    setContatos([]);
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils} locale={getMomentLocale()}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        {/* disableTypography: o wrapper padrão é um <h2> e os Typography abaixo
            virariam <h6> dentro dele */}
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">
            {agendamento?.id ? 'Editar Agendamento' : 'Novo Follow-up'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Preencha os dados para organizar seu próximo contato.
          </Typography>
        </DialogTitle>

        <DialogContent dividers className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className={classes.sectionTitle}>Relacionamento</Typography>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Box flex={1}>
                  <Autocomplete
                    options={clientes}
                    getOptionLabel={(option) => `${option?.NOMECLI || ''}${option?.FANTASIA ? ` - ${option.FANTASIA}` : ''}`}
                    getOptionSelected={(option, value) => String(option?.CODCLI) === String(value?.CODCLI)}
                    filterOptions={(options) => options}
                    loading={loadingClientes}
                    value={selectedCliente}
                    noOptionsText={loadingClientes ? 'Carregando clientes...' : 'Nenhum cliente encontrado'}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const isNearBottom =
                          listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 24;

                        if (isNearBottom) {
                          loadMoreClientes();
                        }
                      },
                    }}
                    onInputChange={(event, inputValue, reason) => {
                      if (!open) {
                        return;
                      }

                      if (reason === 'input') {
                        queueLoadClientes(inputValue);
                        return;
                      }

                      if (reason === 'clear') {
                        queueLoadClientes('', true);
                      }
                    }}
                    onChange={(e, value) => {
                      const nextClienteId = value?.CODCLI || null;
                      const selectedClienteId = selectedCliente?.CODCLI || null;
                      const hasClienteChanged = selectedClienteId !== nextClienteId;

                      setSelectedCliente(value);
                      handleChange('clienteId', nextClienteId);

                      if (hasClienteChanged) {
                        contatosRequestRef.current += 1;
                        handleChange('contatoId', null);
                        setContatos([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        size="small"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingClientes && <CircularProgress size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Box>
                <Tooltip title="Criar novo cliente">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setQuickCreateOpen(true)}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" disabled={!selectedCliente}>
                <InputLabel>Contato</InputLabel>
                <Select
                  // Só usa o valor quando ele existe entre as opções: enquanto a
                  // lista do cliente carrega o Select ficaria fora da faixa.
                  // Quem descarta o contatoId de fato é o loadContatos.
                  value={
                    contatos.some(
                      (contato) => String(contato.id) === String(formData.contatoId)
                    )
                      ? formData.contatoId
                      : ''
                  }
                  onChange={(e) => handleChange('contatoId', e.target.value || null)}
                  label="Contato"
                  size="small"
                >
                  <MenuItem value="">Nenhum</MenuItem>
                  {selectedCliente && contatos.length === 0 && (
                    <MenuItem value="__no_contacts" disabled>
                      Nenhum contato cadastrado para este cliente
                    </MenuItem>
                  )}
                  {contatos.map(contato => {
                    const contatoInfo = getContatoDisplayInfo(contato);

                    return (
                      <MenuItem key={contato.id} value={contato.id}>
                        {contatoInfo ? `${contato.nome} - ${contatoInfo}` : contato.nome}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.sectionTitle}>Detalhes do Follow-up</Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descrição"
                value={formData.descricao}
                onChange={(e) => handleChange('descricao', e.target.value)}
                variant="outlined"
                placeholder="Descreva o objetivo deste follow-up"
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <KeyboardDateTimePicker
                // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: padronizar labels de data/hora para "Data Hora".
                label="Data Hora"
                value={formData.dataInicio ? moment(formData.dataInicio) : null}
                onChange={(date) =>
                  handleChange(
                    'dataInicio',
                    date && moment(date).isValid() ? moment(date).toDate() : null
                  )
                }
                format="DD/MM/YYYY HH:mm"
                ampm={false}
                fullWidth
                inputVariant="outlined"
                size="small"
                invalidDateMessage="Data inválida"
                placeholder="dd/mm/aaaa hh:mm"
                error={!!errors.dataInicio}
                helperText={errors.dataInicio}
              />
            </Grid>

            {!isFollowup && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.tipo}
                    onChange={(e) => handleChange('tipo', e.target.value)}
                    label="Tipo"
                    size="small"
                  >
                    <MenuItem value="reuniao">Reunião</MenuItem>
                    <MenuItem value="ligacao">Ligação</MenuItem>
                    <MenuItem value="visita">Visita</MenuItem>
                    <MenuItem value="followup">Follow-up</MenuItem>
                    <MenuItem value="tarefa">Tarefa</MenuItem>
                    <MenuItem value="outro">Outro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  label="Status"
                  size="small"
                >
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="em_andamento">Em Andamento</MenuItem>
                  <MenuItem value="concluido">Concluído</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={formData.prioridade}
                  onChange={(e) => handleChange('prioridade', e.target.value)}
                  label="Prioridade"
                  size="small"
                >
                  <MenuItem value="baixa">Baixa</MenuItem>
                  <MenuItem value="media">Média</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: remover selecao manual de historico de relacionamento do modal de follow-up. */}

            {formData.status === 'concluido' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Resultado"
                  value={formData.resultado}
                  onChange={(e) => handleChange('resultado', e.target.value)}
                  variant="outlined"
                  placeholder="Descreva o resultado do agendamento..."
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box mt={1}>
                <Typography className={classes.sectionTitle}>Lembrete</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <div className={classes.reminderRow}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.lembrete}
                      onChange={(e) => handleChange('lembrete', e.target.checked)}
                      name="lembrete"
                      color="primary"
                    />
                  }
                  label="Enviar lembrete"
                />
              </div>
            </Grid>

            {formData.lembrete && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minutos antes"
                  value={formData.tempoLembrete}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    handleChange('tempoLembrete', Number.isNaN(value) ? 5 : value);
                  }}
                  variant="outlined"
                  size="small"
                  InputProps={{ inputProps: { min: 5, max: 1440 } }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button onClick={onClose} color="default">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting}
            className={classes.saveButton}
          >
            {submitting ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteQuickCreateModal
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onSave={handleClienteCreated}
        initialName={clienteSearchTerm}
      />
    </MuiPickersUtilsProvider>
  );
};

export default AgendamentoModal;
